import {
    useDispatch,
    useSelector,
} from 'react-redux';
import { useMemo } from 'react';
import { bindActionCreators } from 'redux';
import { IFormHandler } from '../ducks/form';

/**
 * Возвращаемые данные из {@link useFormHandler}
 */
type HookReturnType<TFormHandler extends IFormHandler> = {
    /**
     * Сбрасывает состояние формы.
     */
    reset: TFormHandler['formReset'];
    /**
     * Изменение значения в определенном поле формы.
     * @param field Поле, которое необходимо изменить. Поддерживает формат JSON Path для изменения.
     * @param value Новое значение для поля формы.
     *
     * @example
     * // Изменение поля по имени
     * form.changeField('name', 'New Sprint Name');
     *
     * // Изменение поля в первом объекте массива
     * form.changeField('properties[0].title', 'Custom title');
     *
     * // Изменения поля во вложенном объекте
     * form.changeField('sql.function', 'fnGetTotalAmount');
     */
    changeField: TFormHandler['formChangeField'];
    /**
     * Частичное изменение значений формы.
     */
    change: TFormHandler['formChange'];
    /**
     * Сброс формы в начальное состояние с новыми значениями по-умолчанию.
     */
    initialize: TFormHandler['formInitialize'];
    /**
     * Отправка формы с данными заполненными вручную.
     */
    customSubmit: TFormHandler['submit'];
    /**
     * Флаг, показывающий состояние отправки формы
     */
    formSubmitting: boolean;

	/**
	 * Флаг, показывающий, успешно ли отработала форма
	 */
	formSuccess: boolean;
};

/**
 * Хук для работы с {@link simpleFormHandler} и {@link modalFormHandler} внутри компонентов.
 *
 * Данный хук можно использовать, только внутри компонентов, которые подключены к форме с помощью {@link connectToForm}.
 *
 * Предоставляет дополнительные методы и состояния для работы с формой.
 *
 * @param handler Хендлер реализующий интерфейс {@link IFormHandler}
 * @returns Возвращает объект с действиями формы.
 * @category Hooks
 *
 * @see {@link simpleFormHandler}
 * @see {@link modalFormHandler}
 * @see {@link connectToForm}
 * @see {@link connectToField}
 *
 * @example
 * export default connectToForm<IProps>(function FormComponent({ }) {
 *      const form = useFormHandler(sprintCreateFormHandler);
 *      const didResetClicked = useCallback(() => {
 *          form.formChangeField('counter', 0);
 *      }, [ form ]);
 *
 *      // ...
 * }, sprintCreateFormHandler);
 */
export default function useFormHandler<TFormHandler extends IFormHandler<any>>(handler: TFormHandler): HookReturnType<TFormHandler> {
    const dispatch = useDispatch();
    const formSubmitting = useSelector(handler.formSubmitting);
    const formSuccess = useSelector(handler.formSuccess);

    return useMemo<HookReturnType<TFormHandler>>(() => ({
        reset: bindActionCreators(handler.formReset, dispatch),
        changeField: bindActionCreators(handler.formChangeField, dispatch),
        change: bindActionCreators(handler.formChange, dispatch),
        initialize: bindActionCreators(handler.formInitialize, dispatch),
        customSubmit: bindActionCreators(handler.submit, dispatch),
        formSubmitting,
        formSuccess,
    }), [ dispatch, formSubmitting, formSuccess]);
}
